import { Injectable } from '@angular/core';
import { deepmerge } from '@library/charts/utils';
import { Home } from '@library/store/homes/homes.interface';
import { endOfApiScale, startOfApiScale } from '@library/store/measures/measures';
import { EnumApiScales, EnumMeasureTypes, EnumScales, TimeRange } from '@library/store/measures/measures.interface';
import { Module } from '@library/store/modules/modules.interface';
import { Room } from '@library/store/rooms/rooms.interface';
import { isDefined } from '@library/utils/helpers/store/is-defined';
import { ComponentStore } from '@ngrx/component-store';
import * as moment from 'moment-timezone';
import { YScaleType } from './control-page-main/control-page-compare/cp-compare-sidebar/cp-compare-sidebar.service';

export interface DatasetCard {
  module?: Module;
  room?: Room;
  home?: Home;
  graphType: GraphType;
  datasets: Partial<Record<GraphModes, GraphDataset[]>>;
}

export interface GraphDataset {
  measureType: EnumMeasureTypes;
  type: 'line' | 'bar' | 'custom-bar' | 'stepped-line' | 'wind-angle';
  label: string;
  hidden: boolean,
  module?: Module;
  room?: Room;
  home?: Home;
  yScaleType: YScaleType;
  color: string;
}

export interface ForecastDayData {
  day_ts: number;
  min_temp: number;
  max_temp: number;
  sunset: number;
  sunrise: number;
  rain: number;
  uv: number;
  windgust: number;
  windangle: number;
  winddirection: number;
  wind_direction: number;
  weather_symbol_day: string;
  day_locale: string;
  sun : number
}

export interface ForecastGraphsData {
  rain?: number[][],
  rain_proba?: number[][],
  temperature?: number[][],
  wind?: number[][],
  symbol?: number[][]
}

export enum PollutantType {
  O3 = 'o3',
  PM_25 = 'pm2.5',
  PM_10 = 'pm10',
  CO = 'co',
  NO2 = 'no2',
  SO2 = 'so2',
}

export enum PollutantCategory {
  HAZARDOUS = 'hazardous',
  VERYUNHEALTHY = 'veryUnhealthy',
  UNHEALTHY = 'unhealthy',
  UNHEALTHYFORSENSITIVE = 'unhealthyForSensitive',
  MODERATE = 'moderate',
  GOOD = 'good',
  UNKNOWN = 'unknown'
}

export interface Pollutant {
  type: PollutantType,
  aqi: number,
  category: PollutantCategory,
}

export interface AirQualityData {
  pollutants: Pollutant[],
}

export interface ForecastData {
  forecastDays?: ForecastDayData[],
  forecastGraphs: ForecastGraphsData,
  airqdatav2: AirQualityData,
}

export enum GraphType {
  TEMPERATURE = 'TEMPERATURE',
  HUMIDITY = 'HUMIDITY',
  RAIN = 'RAIN',
  WIND = 'WIND',
  PRESSURE = 'PRESSURE',
  NOISE = 'NOISE',
  CO2 = 'CO2',

  THERM = 'THERM',

  ELEC = 'ELEC',
  ELEC_PEAK_OFFPEAK = 'ELEC_PEAK_OFFPEAK',
  WATER = 'WATER',
  GAS = 'GAS',
}

export enum ConsumptionUnit {
  CONSUMPTION = 'consumption',
  PRICE = 'price',
}

export enum GraphModes {
  DEFAULT = 'default',
  MINMAX = 'minmax',
}

export interface ControlPageOptions {
  graphMode: GraphModes;
}

export interface ControlPageState {
  scale: EnumScales;
  timeRange: TimeRange;
  card: DatasetCard;
  homeConsumptionUnit: ConsumptionUnit;
  forecast: ForecastData;
  options: ControlPageOptions;
}

export enum HeatingModes {
  AWAY = 'away',
  FROSTGUARD = 'hg',
  SCHEDULE = 'schedule',
  OFF = 'off' // NAC BAC
}
export enum ThermoregulationModes {
  HEATING = 'heating',
  COOLING = 'cooling',
  AUTO = 'auto',
}
@Injectable()
export class ControlPageStore extends ComponentStore<ControlPageState> {

  constructor() {
    super({
      scale: null,
      timeRange: null,
      options: {
        graphMode: GraphModes.DEFAULT,
      },
      card: null,
      homeConsumptionUnit: ConsumptionUnit.CONSUMPTION,
      forecast: null
    });
  }

  readonly scale$ = this.select(state => state.scale).pipe(isDefined());
  readonly timeRange$ = this.select(state => state.timeRange).pipe(isDefined());
  readonly options$ = this.select(state => state.options);
  readonly card$ = this.select(state => state.card);
  readonly homeConsumptionUnit$ = this.select(state => state.homeConsumptionUnit);
  readonly forecast$ = this.select(state => state.forecast);
  readonly datasets$ = this.select(state => state.card?.datasets[state.options.graphMode] ?? []);

  readonly setCard = this.updater((state, card: DatasetCard) => {
    return {
      ...state,
      card: card,
    };
  });

  readonly toggleDatasetVisibility = this.updater((state, {datasetIndex}: {datasetIndex: number}) => ({
    ...state,
    card: {
      ...state.card,
      datasets: {
        ...state.card.datasets,
        [state.options.graphMode]: state.card.datasets[state.options.graphMode].map((d, i) => {
          if (i === datasetIndex) {
            d = { ...d, hidden: !d.hidden };
          }
          return d;
        }),
      }
    }
  }));

  readonly selectHomeConsumptionUnit = this.updater((state, unit: ConsumptionUnit) => ({
    ...state,
    homeConsumptionUnit: unit,
  }));

  readonly setForecastData = this.updater((state, { forecast } : { forecast: ForecastData }) => ({
    ...state,
    forecast: {
      forecastDays: forecast.forecastDays,
      forecastGraphs: forecast.forecastGraphs,
      airqdatav2: forecast.airqdatav2,
    }
  }));

  public scaleToApiScale(scale: EnumScales) {
    switch (scale) {
      case EnumScales.DAY:
        return EnumApiScales.FIVE_MINUTES;
      case EnumScales.WEEK:
        return EnumApiScales.THREE_HOURS;
      case EnumScales.MONTH_DAILY:
        return EnumApiScales.ONE_DAY;
      case EnumScales.YEAR:
        return EnumApiScales.ONE_WEEK;
    }
  }

  private lastSelected: {
    timeRange: TimeRange,
    scale: EnumScales,
    [EnumScales.DAY]?: TimeRange,
    [EnumScales.WEEK]?: TimeRange,
    [EnumScales.MONTH_DAILY]?: TimeRange,
    [EnumScales.YEAR]?: TimeRange
  };

  private scales = [EnumScales.DAY, EnumScales.WEEK, EnumScales.MONTH_DAILY, EnumScales.YEAR];
  readonly selectScale = this.updater((state, scale: EnumScales) => {
    let timeRange: TimeRange;

    // Increase scale
    if (this.scales.indexOf(scale) > this.scales.indexOf(state.scale)) {
      if (!this.lastSelected) {
        /**
         * If no period has been selected before it means we are on the current
         * period, thus we use the current period of the selected scale.
         * I.e. we select the timerange based on the current time.
         */
        timeRange = {
          dateBegin: startOfApiScale(moment(), this.scaleToApiScale(scale)),
          dateEnd: endOfApiScale(moment(), this.scaleToApiScale(scale)),
        };
      }
      else {
        /**
         * If a period has beeen selected before, we want to select the period
         * that contains it.
         * I.e. we select the timerange based on the last selected timerange
         */
        if (state.scale === EnumScales.WEEK) {
          /**
           * When we increase the scale coming from the Week scale, we want to select
           * the timerange based on the middle of the week, That way it handles the
           * special case of the week that straddles 2 months or years.
           */
          timeRange = {
            dateBegin: startOfApiScale(this.lastSelected.timeRange.dateBegin.clone().add(60, 'hours'), this.scaleToApiScale(scale)),
            dateEnd: endOfApiScale(this.lastSelected.timeRange.dateBegin.clone().add(60, 'hours'), this.scaleToApiScale(scale)),
          };
        }
        else {
          timeRange = {
            dateBegin: startOfApiScale(this.lastSelected.timeRange.dateBegin.clone(), this.scaleToApiScale(scale)),
            dateEnd: endOfApiScale(this.lastSelected.timeRange.dateBegin.clone(), this.scaleToApiScale(scale)),
          };
        }

        // Save the timerange selected so that we can use it when decreasing scale
        this.lastSelected[scale] = timeRange;
      }
    }
    // Decrease scale
    else {
      /**
       * If no period has been selected before it means we are on the current
       * period, thus we use the current period of the selected scale.
       * I.e. we select the timerange based on the current time.
       */
      if (!this.lastSelected) {
        timeRange = {
          dateBegin: startOfApiScale(moment(), this.scaleToApiScale(scale)),
          dateEnd: endOfApiScale(moment(), this.scaleToApiScale(scale)),
        };
      }
      else {
        /**
         * If a period is selected we want to select the period that is at the
         * start of the current period, thus we use the period of the smallest scale
         *
         */
        const smallestSelectedScale = this.scales.find(s => this.lastSelected[s]);
        timeRange = {
          dateBegin: startOfApiScale(this.lastSelected[smallestSelectedScale].dateBegin.clone(), this.scaleToApiScale(scale)),
          dateEnd: endOfApiScale(this.lastSelected[smallestSelectedScale].dateBegin.clone(), this.scaleToApiScale(scale)),
        };
      }
    }

    return ({
      ...state,
      scale: scale,
      timeRange: timeRange
    });
  });

  readonly selectTimeRange = this.updater((state, timeRange: TimeRange) => {
    const currentTimeRange = {
      dateBegin: startOfApiScale(moment(), this.scaleToApiScale(state.scale)),
      dateEnd: endOfApiScale(moment(), this.scaleToApiScale(state.scale)),
    };

    /**
     * If the selected timerange is the current period, we delete the saved "cache", to
     * reset the behavior of the scale selector
     */
    if (currentTimeRange.dateBegin.isSame(timeRange.dateBegin) && currentTimeRange.dateEnd.isSame(timeRange.dateEnd)) {
      this.lastSelected = null;
    }

    else {
      this.lastSelected = {
        timeRange: timeRange,
        scale: state.scale,
        [state.scale]: timeRange,
      };
    }

    return ({
      ...state,
      timeRange: timeRange,
    });
  });

  readonly setOptions = this.updater((state, options: ControlPageOptions) => {
    return ({
      ...state,
      options: deepmerge(state.options, options),
    });
  });
}
